import {
  Gauge,
  Grid2x2,
  Briefcase,
  Megaphone,
  ScrollText,
  Settings,
  ShipWheel,
  Tags,
} from 'lucide-react';

import type { RouteMetadata } from '@routes/types';

import actions from './actions';

// The route metadata array provides the information for each route and asserts
// the order in which the routing component reads each routing rule.
//
// Note that this information does not contain any of the view component import
// statements that specify the base view associated with each route (as these
// get merged into the object by an operation inside the route manager (defined
// in `/routes/index.js`) so it can be safely imported elsewhere by the
// application as a standalone dataset without any creating any side-effects
// such as circular dependencies.
export const routesWithNavbarItem = [
  {
    path: '',
    chunkName: 'base',
    actions: actions.base,
    navbar: {
      name: 'base',
      title: 'Login',
      permissions: [],
      show: false,
    },
    redirect: {
      primary: { path: 'dashboard', perms: ['listImages', 'viewReports'] },
      secondary: { path: 'system', perms: ['createUser'] },
      tertiary: { path: 'artifacts/image', perms: ['listImages'] },
    },
  },
  {
    path: '/dashboard',
    chunkName: 'dashboard',
    actions: [...actions.base, ...actions.dashboard],
    navbar: {
      name: 'dashboard',
      title: 'Dashboard',
      icon: <Gauge />,
      color: 'teal',
      permissions: [
        {
          action: 'listImages',
          target: '*',
        },
        {
          action: 'viewReports',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/applications',
    chunkName: 'applications',
    actions: [
      ...actions.base,
      ...actions.applications,
      ...actions.vulnAnnotations,
    ],
    entitlement: 'applications',
    navbar: {
      name: 'applications',
      title: 'Applications',
      icon: <Grid2x2 />,
      color: 'teal',
      category: 'assets',
      permissions: [
        {
          action: 'readApplications',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/artifacts/image',
    chunkName: 'artifacts',
    actions: [...actions.base, ...actions['artifacts/image']],
    navbar: {
      name: 'artifacts',
      title: 'Images',
      icon: <Tags />,
      color: 'teal',
      category: 'assets',
      permissions: [
        {
          action: 'listImages',
          target: '*',
        },
        {
          action: 'getImage',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/inventory/kubernetes',
    chunkName: 'inventory',
    actions: [...actions.base, ...actions['inventory/kubernetes']],
    entitlement: 'runtime_inventory',
    navbar: {
      name: 'inventory',
      title: 'Runtime',
      icon: <ShipWheel />,
      category: 'assets',
      permissions: [
        {
          action: 'listImages',
          target: '*',
        },
        {
          action: 'listRuntimeInventories',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/policy',
    chunkName: 'policy',
    actions: [...actions.base, ...actions.policy],
    navbar: {
      name: 'policy',
      title: 'Policies',
      icon: <Briefcase />,
      color: 'teal',
      category: 'general',
      permissions: [
        {
          action: 'listPolicies',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/reports',
    chunkName: 'reports',
    actions: [...actions.base, ...actions.reports],
    navbar: {
      name: 'reports',
      title: 'Reports',
      icon: <ScrollText />,
      color: 'teal',
      category: 'general',
      permissions: [
        {
          action: 'listImages',
          target: '*',
        },
        {
          action: 'viewReports',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/events',
    chunkName: 'events',
    actions: [...actions.base, ...actions.events],
    navbar: {
      name: 'events',
      title: 'Events & Notifications',
      icon: <Megaphone />,
      color: 'teal',
      category: 'general',
      permissions: [
        {
          action: 'listEvents',
          target: '*',
        },
      ],
    },
  },
  {
    path: '/notifications',
    chunkName: 'events',
    actions: [...actions.base, ...actions.notifications],
    navbar: {
      name: 'notifications',
      title: 'Events & Notifications',
      permissions: [
        {
          action: 'getNotificationEndpointConfiguration',
          target: '*',
        },
        {
          action: 'listNotificationEndpointConfigurations',
          target: '*',
        },
        {
          action: 'listNotificationEndpoints',
          target: '*',
        },
      ],
      show: false,
    },
  },
  {
    path: '/system',
    accountScoped: false,
    chunkName: 'system',
    actions: [...actions.base, ...actions.system],
    navbar: {
      name: 'system',
      title: 'System',
      position: 'right',
      icon: <Settings />,
      color: 'teal',
      category: 'general',
      permissions: [
        {
          action: 'createUser',
          target: '*',
        },
      ],
    },
  },
] as const satisfies RouteMetadata[];

const routesMetadata: RouteMetadata[] = [
  ...routesWithNavbarItem,
  // The search results route has no navbar entry; it is reached via the quick
  // search palette, not the nav. It carries the annotation actions because
  // permission keys exist only for the actions a route declares, and its
  // results render the VEX annotation controls.
  {
    path: '/search',
    accountScoped: false,
    chunkName: 'search',
    title: 'Search',
    actions: [...actions.base, ...actions.vulnAnnotations],
  },
  // Wildcard routes, e.g. { path: '(.*)', ... } (must go last)
  {
    path: '(.*)',
    actions: [...actions.base],
  },
];

export default routesMetadata;
